import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell, BarChart,
} from "recharts";
import { bucketLabel, channelColor, channelLabel, eur, num } from "./statsUtils";
import { useTheme } from "@/contexts/ThemeContext";

const NAVY = "#001A38";
const ORANGE = "#FF990A";
const GREEN = "#11A285";
export function usePal() {
  const { theme } = useTheme();
  const dark = theme === "dark";
  return {
    dark,
    navy: dark ? "#5B9BF0" : NAVY,
    grid: dark ? "#1F2E45" : "#EEF0F3",
    axis: { fontSize: 11, fill: dark ? "#8A9AB0" : "#6B7280" },
    tip: {
      contentStyle: { borderRadius: 8, border: `1px solid ${dark ? "#26364F" : "#E5E7EB"}`, fontSize: 12, background: dark ? "#13213A" : "#fff", color: dark ? "#E6EDF5" : undefined, boxShadow: "0 8px 24px rgba(0,26,56,.08)" },
      labelStyle: { color: dark ? "#E6EDF5" : "#111827" },
      itemStyle: dark ? { color: "#E6EDF5" } : undefined,
    },
  };
}

export function ChartCard({ title, subtitle, children, testId, action }) {
  return (
    <section className="rw-card min-w-0 p-3 sm:p-5" data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-foreground sm:text-base">{title}</h2>
          {subtitle && <p className="text-[11px] text-muted-foreground sm:text-xs">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function RevenueOccupancyChart({ series, gran }) {
  const P = usePal();
  const data = series.map((p) => ({ ...p, label: bucketLabel(p.key, gran) }));
  return (
    <ChartCard title="Prihod i zauzeće" subtitle="Stupci: prihod · linija: zauzeće · isprekidano: prošla godina" testId="stats-chart-revenue">
      <div className="h-64 sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid stroke={P.grid} vertical={false} />
            <XAxis dataKey="label" tick={P.axis} tickLine={false} axisLine={false} minTickGap={12} />
            <YAxis yAxisId="r" tick={P.axis} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <YAxis yAxisId="o" orientation="right" tick={P.axis} tickLine={false} axisLine={false} width={38} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip {...P.tip} formatter={(v, n) => (String(n).includes("Zauzeće") ? `${num(v, 1)} %` : eur(v))} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="r" dataKey="revenue" name="Prihod" fill={P.navy} radius={[4, 4, 0, 0]} maxBarSize={28} animationDuration={500} />
            <Line yAxisId="r" dataKey="prev_revenue" name="Prihod (prošla god.)" stroke={P.navy} strokeOpacity={0.45} strokeDasharray="4 4" dot={false} strokeWidth={1.5} />
            <Line yAxisId="o" dataKey="occupancy" name="Zauzeće" stroke={ORANGE} dot={false} strokeWidth={2.2} animationDuration={600} />
            <Line yAxisId="o" dataKey="prev_occupancy" name="Zauzeće (prošla god.)" stroke={ORANGE} strokeOpacity={0.5} strokeDasharray="4 4" dot={false} strokeWidth={1.5} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

export function ChannelsChart({ channels }) {
  const P = usePal();
  const total = channels.reduce((s, c) => s + c.revenue, 0);
  return (
    <ChartCard title="Kanali prodaje" subtitle="Udio u prihodu i broju rezervacija" testId="stats-chart-channels">
      {channels.length === 0 ? <Empty /> : (
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="h-44 w-44 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channels} dataKey="revenue" nameKey="channel" innerRadius="58%" outerRadius="95%" paddingAngle={2} stroke="none" animationDuration={500}>
                  {channels.map((c, i) => <Cell key={c.channel} fill={channelColor(c.channel, i)} />)}
                </Pie>
                <Tooltip {...P.tip} formatter={(v, n) => [eur(v), channelLabel(n)]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="w-full min-w-0 space-y-1.5 text-xs">
            {channels.map((c, i) => (
              <li key={c.channel} className="flex items-center gap-2" data-testid={`stats-channel-${c.channel}`}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: channelColor(c.channel, i) }} />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{channelLabel(c.channel)}</span>
                <span className="font-mono text-foreground">{eur(c.revenue)}</span>
                <span className="w-10 text-right text-muted-foreground">{total ? Math.round((c.revenue / total) * 100) : 0}%</span>
                <span className="w-12 text-right text-muted-foreground">{c.bookings} rez.</span>
              </li>
            ))}
            {channels.some((c) => c.commission) && (
              <li className="pt-1 text-[11px] text-muted-foreground">OTA provizija ukupno: {eur(channels.reduce((s, c) => s + c.commission, 0), 2)}</li>
            )}
          </ul>
        </div>
      )}
    </ChartCard>
  );
}

export function WeekdayChart({ weekdays }) {
  const P = usePal();
  return (
    <ChartCard title="Dani u tjednu" subtitle="Zauzeće i prosječna cijena noćenja" testId="stats-chart-weekdays">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={weekdays} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid stroke={P.grid} vertical={false} />
            <XAxis dataKey="day" tick={P.axis} tickLine={false} axisLine={false} />
            <YAxis yAxisId="o" tick={P.axis} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${num(v, v < 10 ? 1 : 0)}%`} />
            <YAxis yAxisId="a" orientation="right" tick={P.axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => `${Math.round(v)}€`} />
            <Tooltip {...P.tip} formatter={(v, n) => (n === "Zauzeće" ? `${num(v, 1)} %` : eur(v, 2))} />
            <Bar yAxisId="o" dataKey="occupancy" name="Zauzeće" fill={GREEN} radius={[4, 4, 0, 0]} maxBarSize={32} animationDuration={500} />
            <Line yAxisId="a" dataKey="adr" name="ADR" stroke={ORANGE} strokeWidth={2.2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

export function DistributionChart({ title, subtitle, data, color, testId }) {
  const P = usePal();
  return (
    <ChartCard title={title} subtitle={subtitle} testId={testId}>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
            <CartesianGrid stroke={P.grid} vertical={false} />
            <XAxis dataKey="bucket" tick={P.axis} tickLine={false} axisLine={false} />
            <YAxis tick={P.axis} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
            <Tooltip {...P.tip} formatter={(v) => [`${v} rez.`, "Broj"]} />
            <Bar dataKey="count" fill={color || P.navy} radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={500} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

const Empty = () => <p className="py-10 text-center text-sm text-muted-foreground">Nema podataka za odabrano razdoblje.</p>;
